/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ShopperProducts } from '@/scapi';

type Category = ShopperProducts.schemas['Category'];

function category(data: Category): Category {
    const categories = data.categories?.map(category);

    return {
        c_showInMenu: true,
        ...data,
        categories,
        onlineSubCategoriesCount: categories?.length ?? data.onlineSubCategoriesCount ?? 0,
    };
}

/**
 * Sanctioned per CRCB2B-650's AC: when the catalog root category promise fails, resolves empty,
 * or isn't configured with real data yet, the header renders this placeholder structure instead
 * of an empty or broken nav. Named after the two root-level categories the real catalog is
 * expected to have ("Shop", "Dealer Support" — see design.md decision 3) so the placeholder
 * validates the same shape real data will take, not arbitrary demo content.
 */
export const PLACEHOLDER_ROOT_CATEGORIES: Category[] = [
    category({
        id: 'placeholder-shop',
        name: 'Shop',
        categories: [
            {
                id: 'placeholder-shop-drivetrain',
                name: 'Drivetrain',
                categories: [
                    { id: 'placeholder-shop-drivetrain-groupsets', name: 'Groupsets' },
                    { id: 'placeholder-shop-drivetrain-cassettes', name: 'Cassettes' },
                    { id: 'placeholder-shop-drivetrain-cranksets', name: 'Cranksets' },
                ],
            },
            {
                id: 'placeholder-shop-brakes',
                name: 'Brakes',
                categories: [
                    { id: 'placeholder-shop-brakes-levers', name: 'Levers' },
                    { id: 'placeholder-shop-brakes-rotors', name: 'Rotors' },
                ],
            },
            {
                id: 'placeholder-shop-wheels',
                name: 'Wheels',
                categories: [
                    { id: 'placeholder-shop-wheels-road', name: 'Road' },
                    { id: 'placeholder-shop-wheels-mountain', name: 'Mountain' },
                ],
            },
        ],
    }),
    category({
        id: 'placeholder-dealer-support',
        name: 'Dealer Support',
        categories: [],
    }),
];
